// Fixed score multipliers for non-slider difficulty add-ons
export const SCORE_MULTIPLIERS = {
  isFlipped: 1.05,
  isInverted: 1.1,
  isMosaic: 1.05,
  isNoColor: 1.07,
};

// Dynamic multiplier ranges for slider-based add-ons
const BLUR_MULTIPLIER = {
  min: 5,    // 5px blur
  max: 30,   // 30px blur
  multiplierMin: 1.2,
  multiplierMax: 1.8,
};

const CROP_MULTIPLIER = {
  min: 10,   // 10% crop (show 90%)
  max: 80,   // 80% crop (show 20%)
  multiplierMin: 1.05,
  multiplierMax: 1.7,
};

const TIME_LIMIT_MULTIPLIER = {
  maxTime: 5000,        // 5000ms (easiest)
  minTime: 100,         // 100ms (hardest)
  maxMultiplier: 1.3,   // 100ms = 1.3x (hardest = highest multiplier)
  minMultiplier: 1.05,  // 5000ms = 1.05x (easiest = lowest multiplier)
};

/**
 * Calculates the blur multiplier based on blur amount
 * Linear interpolation between 1.2x (5px) and 2.2x (30px)
 */
const calculateBlurMultiplier = (blurAmount: number): number => {
  const { min, max, multiplierMin, multiplierMax } = BLUR_MULTIPLIER;
  const normalized = Math.max(0, Math.min(1, (blurAmount - min) / (max - min)));
  return multiplierMin + normalized * (multiplierMax - multiplierMin);
};

/**
 * Calculates the crop multiplier based on crop percentage
 * Linear interpolation between 1.05x (10% crop) and 1.7x (80% crop)
 */
const calculateCropMultiplier = (cropPercentage: number): number => {
  const { min, max, multiplierMin, multiplierMax } = CROP_MULTIPLIER;
  const normalized = Math.max(0, Math.min(1, (cropPercentage - min) / (max - min)));
  return multiplierMin + normalized * (multiplierMax - multiplierMin);
};

/**
 * Calculates the time limit multiplier based on time in milliseconds
 * Uses logarithmic interpolation for a smooth exponential-like curve:
 * - 5000ms (5s) ≈ 1.05x
 * - 1000ms (1s) ≈ 1.15x
 * - 100ms ≈ 1.3x
 * 
 * Formula: multiplier = maxMult - (maxMult - minMult) * log(time/minTime) / log(maxTime/minTime)
 * This creates a curve where shorter times dramatically increase difficulty
 */
const calculateTimeLimitMultiplier = (timeLimitMs: number): number => {
  const { maxTime, minTime, maxMultiplier, minMultiplier } = TIME_LIMIT_MULTIPLIER;
  
  // Clamp time to valid range
  const clampedTime = Math.max(minTime, Math.min(maxTime, timeLimitMs));
  
  // Logarithmic interpolation (inverse relationship: less time = higher multiplier)
  const logRatio = Math.log(clampedTime / minTime) / Math.log(maxTime / minTime);
  const multiplier = maxMultiplier - (maxMultiplier - minMultiplier) * logRatio;
  
  return multiplier;
};

interface DifficultySettings {
  isBlurry: boolean;
  blurAmount: number;
  hasTimeLimit: boolean;
  timeLimitMs: number;
  isCropped: boolean;
  cropPercentage: number;
  isFlipped: boolean;
  isInverted: boolean;
  isMosaic: boolean;
  isNoColor: boolean;
}

export const calculateScoreMultiplier = (settings: DifficultySettings): number => {
  let multiplier = 1.0;

  // Dynamic multipliers based on slider values
  if (settings.isBlurry) {
    multiplier *= calculateBlurMultiplier(settings.blurAmount);
  }
  if (settings.isCropped) {
    multiplier *= calculateCropMultiplier(settings.cropPercentage);
  }
  if (settings.hasTimeLimit) {
    multiplier *= calculateTimeLimitMultiplier(settings.timeLimitMs);
  }

  // Fixed multipliers
  if (settings.isFlipped) multiplier *= SCORE_MULTIPLIERS.isFlipped;
  if (settings.isInverted) multiplier *= SCORE_MULTIPLIERS.isInverted;
  if (settings.isMosaic) multiplier *= SCORE_MULTIPLIERS.isMosaic;
  if (settings.isNoColor) multiplier *= SCORE_MULTIPLIERS.isNoColor;

  return multiplier;
};

